import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import moment from "moment";
import ClientsTariff from "../../../components/tables/ClientsTariff";
import { Modal, Table, Typography, Button, Menu, Dropdown } from "antd";
import NewContract from "./NewContract";
import EditContract from "./EditContract";
import EditContractVersion from "./EditContractVersion";
import NewVersion from "./NewVersion";
import FormHeaderWrapper from "../../../components/forms/FormHeader.style";
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import RestrictedComponent from "../../../components/util/RestrictedComponent";

const { Title, Text } = Typography;

const ContractsList = (props) => {
  const {
    items,
    loading,
    changeLoading,
    deleteLoading,
    fetchPage,
    loadRecord,
    reset,
    deleteRecord,
    createRecord,
    updateRecord,
    updateRecordVersion,
    deleteRecordVersion,
    createRecordVersion,
    activeContract,
  } = props;
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();
  const [visible, setVisible] = useState(false);
  const [newContractVisible, setNewContractVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editVersionVisible, setEditVersionVisible] = useState(false);
  const [newVersionVisible, setNewVersionVisible] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    dispatch(fetchPage());
  }, [dispatch]);

  const onAddClick = () => {
    setVisible(true);
    setNewContractVisible(true);
  };

  const onEdit = (record) => {
    dispatch(loadRecord(record.id));
    setVisible(true);
    setEditVisible(true);
  };

  const onEditVersion = (record) => {
    dispatch(loadRecord(record.id));
    setVisible(true);
    setEditVersionVisible(true);
  };

  const onAddVersion = (record) => {
    dispatch(loadRecord(record.id));
    setVisible(true);
    setNewVersionVisible(true);
  };

  const onDeleteVersion = (record) => {
    const title = intl.formatMessage({
      id: "app.contracts.deleteContractTitle",
    });
    const content = intl.formatMessage({
      id: "app.contracts.deleteContractContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirm(record);
      },
      onCancel() {},
    });
  };
  const onConfirm = async (record) => {
    const action = await dispatch(deleteRecord(record.id));
    if (!action.error) {
      dispatch(fetchPage());
    }
  };

  const handleAdminMenuClick = (e, record) => {
    switch (e.key) {
      case "1":
        onEdit(record);
        break;

      case "2":
        onEditVersion(record);
        break;

      case "3":
        onAddVersion(record);
        break;
      case "4":
        onDeleteVersion(record);
        break;

      default:
        break;
    }
  };

  const hide = () => {
    setVisible(false);
    setNewContractVisible(false);
    setEditVisible(false);
    setEditVersionVisible(false);
    setNewVersionVisible(false);
    dispatch(fetchPage());
    dispatch(reset());
  };

  return (
    <div>
      {contextHolder}
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            <FormattedMessage id={`app.settings.contracts`} />
          </Title>
          <RestrictedComponent
            resource={"organization.settings"}
            action={"editor"}
            comp={null}
          >
            <Button type="primary" onClick={onAddClick}>
              <FormattedMessage id={`app.actions.addContract`} />
            </Button>
          </RestrictedComponent>
        </div>
      </FormHeaderWrapper>

      <Table
        bordered
        rowKey={(record) => record.id}
        scroll={{ y: window.innerHeight - 526 }}
        dataSource={items}
        pagination={false}
        columns={[
          {
            title: intl.formatMessage({
              id: "app.contracts.name",
            }),
            dataIndex: "name",
            render(text, record) {
              return (
                <div style={{ display: "flex" }}>
                  <Text>{text}</Text>
                </div>
              );
            },
            sortDirections: ["ascend", "descend", "ascend"],
            defaultSortOrder: "descend",
            sorter: (a, b) => a.name.localeCompare(b.name),
          },
          {
            title: intl.formatMessage({
              id: "app.contracts.description",
            }),
            dataIndex: "description",
            render(text, record) {
              return (
                <div style={{ display: "flex" }}>
                  <Text>{text}</Text>
                </div>
              );
            },
          },

          {
            title: intl.formatMessage({
              id: "app.contracts.client_tariff_id",
            }),
            dataIndex: "contract_versions",
            width: "140px",
            render(text, record) {
              const filtered = text
                ? text.filter((i) => moment(i.effective_from) < moment())
                : [];
              const selected =
                filtered.length > 0
                  ? filtered.reduce((a, b) =>
                      a.effective_from > b.effective_from ? a : b
                    )
                  : [];
              return (
                selected && <ClientsTariff text={selected.client_tariff_id} />
              );
            },
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: (a, b) => {
              const filteredA = a.contract_versions.filter(
                (i) => moment(i.effective_from) < moment()
              );
              const selectedA = filteredA.reduce((a, b) =>
                a.effective_from > b.effective_from ? a : b
              );

              const filteredB = b.contract_versions.filter(
                (i) => moment(i.effective_from) < moment()
              );
              const selectedB = filteredB.reduce((a, b) =>
                a.effective_from > b.effective_from ? a : b
              );
              return selectedA.base_rate - selectedB.base_rate;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.contracts.effective_from",
            }),
            dataIndex: "contract_versions",
            render: (text, record, index) => {
              //filter future effective versions
              const filtered = text
                ? text.filter((i) => moment(i.effective_from) < moment())
                : [];

              const selected =
                filtered.length > 0
                  ? moment(
                      filtered.reduce((a, b) =>
                        a.effective_from > b.effective_from ? a : b
                      ).effective_from
                    ).format("MM/YYYY")
                  : moment(
                      text.reduce((a, b) =>
                        a.effective_from < b.effective_from ? a : b
                      ).effective_from
                    ).format("MM/YYYY");
              return <Text>{selected}</Text>;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.contracts.effective_to",
            }),
            dataIndex: "effective_to",
            render: (text, record, index) => {
              return <Text>{text && moment(text).format("MM/YYYY")}</Text>;
            },
          },
          {
            title: intl.formatMessage({
              id: "app.contracts.actions",
            }),
            width: "130px",
            render(text, record) {
              return (
                <RestrictedComponent
                  resource={"organization.settings"}
                  action={"editor"}
                  comp={null}
                >
                  <div style={{ display: "flex" }}>
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          {
                            label: intl.formatMessage({
                              id: "app.contracts.editContract",
                            }),
                            key: "1",
                            icon: <EditOutlined />,
                          },
                          {
                            label: intl.formatMessage({
                              id: "app.contracts.editContractVersion",
                            }),
                            key: "2",
                            icon: <EditOutlined />,
                          },
                          {
                            label: intl.formatMessage({
                              id: "app.contracts.updateContract",
                            }),
                            key: "3",
                            icon: <PlusOutlined />,
                          },
                          {
                            label: intl.formatMessage({
                              id: "app.contracts.deleteContract",
                            }),
                            key: "4",
                            icon: <DeleteOutlined />,
                            style: { color: "red" },
                          },
                        ],
                        onClick: (e) => handleAdminMenuClick(e, record),
                      }}
                    >
                      <Button type="link">
                        {intl.formatMessage({
                          id: "app.employees.options",
                        })}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  </div>
                </RestrictedComponent>
              );
            },
          },
        ]}
        loading={loading}
      />
      <Modal
        closable={false}
        maskClosable={false}
        open={visible}
        footer={null}
        onCancel={hide}
        title={null}
        width={"90%"}
        styles={{
          body: {
            padding: 0,
          },
        }}
        destroyOnClose={true}
      >
        {newContractVisible ? (
          <NewContract
            createRecord={createRecord}
            hide={hide}
            loading={changeLoading}
            addText={<FormattedMessage id="app.contracts.addNewContract" />}
          />
        ) : null}
        {editVisible ? (
          <EditContract
            updateRecord={updateRecord}
            activeContract={activeContract}
            loading={changeLoading}
            hide={hide}
            addText={<FormattedMessage id="app.contracts.editContract" />}
          />
        ) : null}

        {editVersionVisible ? (
          <EditContractVersion
            updateRecordVersion={updateRecordVersion}
            deleteRecordVersion={deleteRecordVersion}
            loading={changeLoading}
            activeContract={activeContract}
            hide={hide}
            addText={
              <FormattedMessage id="app.contracts.editContractVersion" />
            }
          />
        ) : null}

        {newVersionVisible ? (
          <NewVersion
            createRecordVersion={createRecordVersion}
            activeContract={activeContract}
            loading={changeLoading}
            hide={hide}
            addText={<FormattedMessage id="app.contracts.updateContract" />}
          />
        ) : null}
      </Modal>
    </div>
  );
};
export default ContractsList;
